'use client';

/**
 * Dev quick-login — one-click sign-in as a seeded dev account.
 *
 * Unlike DemoQuickLogin (demo-mode cookie shim, no Supabase), each button here
 * performs a REAL Supabase password sign-in via devLoginAction using the
 * server-only DEV_LOGIN_PASSWORD. The login page renders this only when
 * NEXT_PUBLIC_ENVIRONMENT=dev, demo mode is off and DEV_LOGIN_PASSWORD is set,
 * so it never appears in staging or production.
 *
 * WHY THIS IS A CLIENT COMPONENT
 * It used to be a server component rendering bare submit buttons, which meant
 * a click produced no feedback at all: devLoginAction round-trips to Supabase
 * and only then redirects, so for a second or more the page looked like it had
 * ignored you, and the natural response was to click again — starting a second
 * sign-in that races the first. Each button now reports its own form's pending
 * state (the same `useFormStatus` pattern as SignInSubmitButton), which also
 * lets the whole set disable while one is in flight.
 */

import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useFormStatus } from 'react-dom';

import { Button } from '@/components/ui/button';

import { devLoginAction } from './actions';

// Each persona lands where its role actually works: staff into the analyst
// console (CRM straight to its lane), subscribers onto the dashboard. An
// explicit ?next= from the login URL still wins.
const ACCOUNTS = [
  {
    email: 'demo+premium@stocksinglobal.com.au',
    label: 'Premium',
    name: 'Priya Mehta',
    home: '/dashboard',
  },
  {
    email: 'demo+pro@stocksinglobal.com.au',
    label: 'Pro',
    name: 'Marcus Park',
    home: '/dashboard',
  },
  {
    email: 'demo+basic@stocksinglobal.com.au',
    label: 'Basic',
    name: 'Sarah Chen',
    home: '/dashboard',
  },
  {
    email: 'hersh@stocksinglobal.com.au',
    label: 'CIO',
    name: 'Hersh Oberoi',
    home: '/analyst/queue',
  },
  {
    email: 'kieran@stocksinglobal.com.au',
    label: 'Analyst',
    name: 'Kieran Thomas',
    home: '/analyst/queue',
  },
  {
    email: 'campaign+demo@stocksinglobal.com.au',
    label: 'CRM',
    name: 'Tahlia Brooks',
    home: '/analyst/crm',
  },
  {
    email: 'admin@stocksinglobal.com.au',
    label: 'Admin',
    name: 'System Admin',
    home: '/analyst/queue',
  },
] as const;

type Account = (typeof ACCOUNTS)[number];

export function DevQuickLogin({ next }: { next?: string }) {
  // Which account is mid-sign-in, so every OTHER button can disable itself.
  // Derived from the pending forms rather than set on click, so it clears
  // itself if the action fails and returns instead of redirecting.
  const [busyEmail, setBusyEmail] = useState<string | null>(null);
  const busyAccount = ACCOUNTS.find((a) => a.email === busyEmail) ?? null;

  return (
    <div className="mt-5 rounded-[4px] border border-dashed border-[var(--color-hair-2)] p-3">
      <p className="mb-2 text-center font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--color-faint)]">
        Dev — one-click login (real Supabase auth)
      </p>
      <div className="grid grid-cols-3 gap-2">
        {ACCOUNTS.map((a) => (
          <form key={a.email} action={devLoginAction}>
            <input type="hidden" name="email" value={a.email} />
            <input type="hidden" name="next" value={next || a.home} />
            <DevLoginButton account={a} anyBusy={busyEmail !== null} onBusyChange={setBusyEmail} />
          </form>
        ))}
      </div>

      {/* Named confirmation of WHO is being signed in. A spinner alone tells
          you something is happening; this tells you the right thing is. */}
      <p
        role="status"
        aria-live="polite"
        className="mt-2 h-4 text-center font-mono text-[10px] tracking-[0.08em] text-[var(--color-faint)]"
      >
        {busyAccount ? `Signing in as ${busyAccount.name}…` : ''}
      </p>
    </div>
  );
}

/**
 * One account button. Lives inside its own <form> so `useFormStatus` reports
 * that form's submission — React resets it when the action settles, including
 * on failure, so a failed sign-in can never leave the set permanently disabled.
 */
function DevLoginButton({
  account,
  anyBusy,
  onBusyChange,
}: {
  account: Account;
  anyBusy: boolean;
  onBusyChange: (updater: (current: string | null) => string | null) => void;
}) {
  const { pending } = useFormStatus();
  const { email } = account;

  useEffect(() => {
    // Claim the busy slot while pending; release it only if we still hold it,
    // so a settling form can't clear another form's in-flight state.
    onBusyChange((current) => (pending ? email : current === email ? null : current));
  }, [pending, email, onBusyChange]);

  return (
    <Button
      type="submit"
      variant="secondary"
      size="sm"
      className="w-full"
      // Every button locks while any sign-in is running: two concurrent
      // password sign-ins would race, and whichever resolved last would decide
      // which role you land in.
      disabled={anyBusy}
      aria-busy={pending}
      title={`${account.name} — ${account.email}`}
    >
      {pending ? (
        <>
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {account.label}
        </>
      ) : (
        account.label
      )}
    </Button>
  );
}
